<template>
  <div class="profil-page">
    <div class="profil-container">
      <!-- Header Card -->
      <div class="profil-header">
        <div class="profil-header-bg"></div>
        <div class="profil-header-content">
          <div class="profil-avatar-wrap">
            <img :src="image" alt="User avatar" class="profil-avatar" />
            <div class="profil-avatar-badge">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20 6L9 17l-5-5"/>
              </svg>
            </div>
          </div>
          <div class="profil-header-info">
            <h1 class="profil-header-name">{{ currentUser.fullName || 'Profile Settings' }}</h1>
            <p class="profil-header-role">{{ currentUser.role && currentUser.role.name ? currentUser.role.name : '' }} <span v-if="currentUser.company"> &middot; {{ currentUser.company }}</span></p>
          </div>
          <div class="profil-header-action" v-if="$f.getAccessPermission(currentUser, 'userSessions', 'create')">
            <button :disabled="loading" @click="checkLogs()" class="profil-security-btn loidingconfirm relative">
              <loading :active.sync="loading" :is-full-page="fullPage"></loading>
              <svg class="profil-btn-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0c-2.995 2.995-7.486 4-11 4 0 8.582 5.068 16.097 11 20 5.932-3.903 11-11.418 11-20-3.514 0-8.005-1.005-11-4zm-8.912 5.894c2.455-.246 5.912-1.012 8.912-3.25v18.906c-4-3.063-8.254-8.604-8.912-15.656z"/>
              </svg>
              <span>Active Sessions</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Alert Message -->
      <div v-if="messages" class="profil-alert">
        <div class="profil-alert-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        </div>
        <span>{{ messages }}</span>
      </div>

      <!-- Inactive Warning -->
      <div v-if="currentUser.status == 'inactive'" class="profil-warning">
        <div class="profil-warning-icon">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="profil-warning-title">Action Required</p>
          <p class="profil-warning-text">You must change your password to continue</p>
        </div>
      </div>

      <!-- Form Card: Personal Info -->
      <div class="profil-card">
        <div class="profil-card-header">
          <h2 class="profil-card-title">Personal Information</h2>
          <p class="profil-card-desc">Update your personal details here</p>
        </div>
        <div class="profil-card-body">
          <!-- Row: Email & Role -->
          <div class="profil-grid">
            <div class="profil-field">
              <label class="profil-label">Email</label>
              <div class="profil-input-wrap profil-input-disabled">
                <i class="material-icons profil-input-icon">mail_outline</i>
                <span class="profil-input-text">{{ currentUser.email }}</span>
              </div>
            </div>
            <div class="profil-field">
              <label class="profil-label">Role</label>
              <div class="profil-input-wrap profil-input-disabled">
                <i class="material-icons profil-input-icon">verified_user</i>
                <input type="text" v-model="currentUser.role.name" class="profil-input" placeholder="Role" disabled />
              </div>
            </div>
          </div>

          <!-- Row: Full Name & Phone -->
          <div class="profil-grid">
            <div class="profil-field">
              <label class="profil-label">Full Name <span class="profil-required">*</span></label>
              <div class="profil-input-wrap">
                <i class="material-icons profil-input-icon">person_outline</i>
                <input type="text" v-model="currentUser.fullName" class="profil-input" placeholder="Enter your full name" />
              </div>
            </div>
            <div class="profil-field">
              <label class="profil-label">Phone</label>
              <div class="profil-input-wrap">
                <i class="material-icons profil-input-icon">phone</i>
                <input type="text" v-model="currentUser.phone" class="profil-input" placeholder="Enter your phone number" />
              </div>
            </div>
          </div>

          <!-- Row: Username & Company -->
          <div class="profil-grid">
            <div class="profil-field">
              <label class="profil-label">Username</label>
              <div v-if="isDisabled" class="profil-input-wrap profil-input-disabled">
                <i class="material-icons profil-input-icon">person_outline</i>
                <span class="profil-input-text">{{ currentUser.username }}</span>
              </div>
              <div v-else class="profil-input-wrap">
                <i class="material-icons profil-input-icon">person_outline</i>
                <input type="text" v-model="currentUser.username" class="profil-input" placeholder="Choose a username" @keydown.space="(event) => event.preventDefault()" @keydown="nameKeydown($event)" />
              </div>
            </div>
            <div class="profil-field">
              <label class="profil-label">Company</label>
              <div class="profil-input-wrap">
                <i class="material-icons profil-input-icon">business</i>
                <input type="text" v-model="currentUser.company" class="profil-input" placeholder="Enter company name" />
              </div>
            </div>
          </div>

          <!-- RIB (Seller only) -->
          <div v-if="currentUser.type=='Seller'" class="profil-grid">
            <div class="profil-field">
              <label class="profil-label">RIB <span class="profil-required">*</span></label>
              <div class="profil-input-wrap">
                <i class="material-icons profil-input-icon">account_balance_wallet</i>
                <input type="text" v-model="currentUser.rib" class="profil-input" placeholder="Enter your RIB" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Form Card: Change Password -->
      <div class="profil-card">
        <div class="profil-card-header profil-card-header-toggle" @click="seen = !seen">
          <div>
            <h2 class="profil-card-title">Change Password</h2>
            <p class="profil-card-desc">Update your password for better security</p>
          </div>
          <button class="profil-toggle-btn" :class="{ 'profil-toggle-open': seen }">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
        </div>
        <div v-if="seen" class="profil-card-body">
          <div class="profil-grid">
            <div class="profil-field">
              <label class="profil-label">New Password</label>
              <div class="profil-input-wrap">
                <i class="material-icons profil-input-icon">lock_outline</i>
                <input type="password" v-model="currentUser.password" class="profil-input" placeholder="Enter new password" />
              </div>
            </div>
            <div class="profil-field">
              <label class="profil-label">Confirm Password</label>
              <div class="profil-input-wrap">
                <i class="material-icons profil-input-icon">lock_outline</i>
                <input type="password" v-model="currentUser.confirmPassword" class="profil-input" placeholder="Confirm new password" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Save Button -->
      <div class="profil-actions">
        <button @click="update" class="profil-save-btn">
          <svg class="profil-btn-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
            <polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
          </svg>
          Save Changes
        </button>
      </div>
    </div>

    <!-- Modal: Session Logs -->
    <vue-modal-2 name="myModalLogs" :headerOptions="{title: 'Where youre logged in'}" :noFooter="true" modalSize="lg">
      <div class="flex flex-col w-full h-auto">
        <div class="bg-white rounded px-2 pb-2 flex flex-col relative">
          <loading :active.sync="loading" :is-full-page="fullPage"></loading>
          <div v-for="(item, index) in userSessions" :key="index" class="tr-t">
            <div class="tr-t1">
              <div class="_ohe lfloat"><div class="_a7x img _8o _8r"><img alt="Macintosh" :src="getFlag(item.device)" class="img"></div></div>
              <div class="tr-t2">
                <div class="">
                  <div class="_ohe lfloat">
                    <div>
                      <span class="_3o7u">
                        {{ item.device && item.device.name?item.device.type=='smartphone'?'Phone':item.device.name:'Device type unknown' }} &middot;
                        <span data-hover="tooltip" id="js_1yq">{{ item.info.city||'City unknown'  }}, {{ $f.getCountryNameByCode(options,item.info.country)  }}</span>
                      </span>
                      <span class="_1aml _50f8">{{ item.device?item.device.client.name:'REST API' }} ,</span>
                    </div>
                  </div>
                  <span class="_1anp _1aml date-tr">{{ item.date }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer px-5 border0-t text-right">
          <div class="flex-auto flex flex-row-reverse">
            <button :disabled="loading" @click="RefreshToken" class="w-1/3 p-3 btn-action rounded bg-blue-600 hover:bg-blue-500 text-white font-semibold focus:outline-none">Log out of all sessions</button>
            <button :disabled="loading" @click="$vm2.close('myModalLogs')" class="w-1/3 p-3 mr-4 rounded bg-gray-600 hover:bg-gray-500 text-white font-semibold focus:outline-none">Close</button>
          </div>
        </div>
      </div>
    </vue-modal-2>
  </div>
</template>

<script>
import DivFilter from "../components/DivFilter.vue";
import image from "@/assets/shipsen-man.jpg";
import Button from '../components/~/button.vue';
export default {
  components: { DivFilter, Button },
  name: "Profil",
  props: {
     currentUser: { type: Object},
     idWharhouse: { type: Object}
   },
  data() {
    return {
      isDisabled: false,
      loading:false,
      fullPage:false,
      show: false,
      image: 'https://img.icons8.com/fluency/96/user-male-circle--v1.png',
      messages:'',
      seen: false,
      userSessions:[],
      options: this.$countries,
    };
  },
  async mounted() {
     this.token = this.$jwtService.verifyToken();
     this.getUser();
  },
  methods: {
    getFlag(device){
     let src='https://img.icons8.com/carbon-copy/37/null/api-settings.png';
      if(device){
          src='https://www.facebook.com/images/si/security_settings/wyli/device-mac.png';
          if(device.type){
            if(device.type=='smartphone') src='https://www.facebook.com/images/si/security_settings/wyli/device-generic-phone.png';
            else{
              if(device.name=='Mac') src='https://www.facebook.com/images/si/security_settings/wyli/device-mac.png';
              else src='https://www.facebook.com/images/si/security_settings/wyli/device-windows.png';
            }
          }
      }
       return src;
    },
    async RefreshToken(){
          this.loading=true;
          const dataa = await this.$server.update("userSessions",{RefreshToken:true});
          console.log('dataa RefreshToken-----',dataa)
          this.loading=false;
          if(dataa && dataa._id) {
             alert("All Session deleted", "success");
             this.$vm2.close('myModalLogs');
          }else  alert(dataa.content||dataa, "warning");
    },
    async checkLogs(){
      this.loading=true;
      const res = await this.$server.search("userSessions", {});
      if (res && res.content.results) {
        this.userSessions = res.content.results;
        this.userSessions.forEach((value, key) => {
          value.date=this.$moment(value.date).format("MMMM Do YYYY, h:mm A");
        });
      } else this.userSessions = [];
      this.loading=false;
      if(this.userSessions.length>0) this.$vm2.open('myModalLogs');
      else  alert('No activity logs found', "warning");
    },
     getUser() {
      if(this.currentUser.username) this.isDisabled = true;
      this.checkUserMessage();
    },
    showBar() {
      if (this.show) this.show = false;
      else this.show = true;
    },
    checkUserMessage(){
       this.messages='';
       this.seen=false;
       if(this.currentUser.status=='inactive') this.seen=true;
       if(this.currentUser.type=="Seller" && (!this.currentUser.fullName || !this.currentUser.rib)){
          if(!this.currentUser.fullName) this.messages='full Name is required ';
          if(!this.currentUser.rib){
               if(!this.currentUser.fullName) this.messages +='&& ';
               this.messages +='RIB is required ';
          }
        }
    },
    async update() {
      console.log('this.checkData()---',this.checkData())
      if(this.checkData()){
        console.log('this.currentUser------',this.currentUser)
      this.$confirm("Are you sure ?").then(async (res) => {
        if (res) {
          if(this.currentUser.password) this.currentUser.password=this.currentUser.password.trim();
          if(this.currentUser.confirmPassword) this.currentUser.confirmPassword=this.currentUser.confirmPassword.trim();
           console.log('this.currentUser.--------',this.currentUser)
          if (this.currentUser.status == "inactive" && this.currentUser.password) {
            if (this.checkPassword()) {
              this.currentUser.status = "active";
              this.currentUser.Updatestatus=true;
              const dataa = await this.$server.update("users", this.currentUser);
              console.log('data- 11111----',dataa)
              if (dataa && dataa._id) {
                alert("Profil updated", "success");
                this.logout();
                this.currentUser.password = "";
                this.currentUser.confirmPassword = "";
              }else alert(dataa, "warning");
            }
          } else {
            const data = await this.$server.update("users", this.currentUser);
            if (data && data._id) {
              alert("Profil updated", "success");
              this.currentUser.password = "";
              this.currentUser.confirmPassword = "";
              this.getUser();
            }else alert(data, "warning");
          }
        }
      });
      }else alert('You must fill all the fields !!', "warning");
    },
    checkPassword() {
      if (this.currentUser.password == this.currentUser.confirmPassword) {
        return true;
      } else {
        alert("Confirmation password does not match ", "warning");
        return false;
      }
    },
    checkData() {
      if(this.currentUser.type == "Seller"){
         if(this.currentUser.rib &&  this.currentUser.email  && this.currentUser.fullName) return true;
         else return false;
      }else{
         if(this.currentUser.email) return true;
         else return false;
      }
    },
    logout() {
      this.$jwtService.destroyToken();
      location.href = "/";
    },
    nameKeydown(e) {
      if (/^\W$/.test(e.key)) {
        e.preventDefault();
      }
    },
  },
};
</script>

<style scoped>
/* ===== Page Layout ===== */
.profil-page {
  padding: 80px 16px 40px;
  min-height: 100vh;
  background: #f0f2f5;
}
.profil-container {
  max-width: 800px;
  margin: 0 auto;
}

/* ===== Header Card ===== */
.profil-header {
  position: relative;
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
}
.profil-header-bg {
  height: 100px;
  background: linear-gradient(135deg, #4285f4 0%, #5a9cf6 60%, #7aaffc 100%);
}
.profil-header-content {
  display: flex;
  align-items: center;
  padding: 0 28px 24px;
  flex-wrap: wrap;
  gap: 16px;
}
.profil-avatar-wrap {
  position: relative;
  margin-top: -36px;
}
.profil-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  background: #fff;
}
.profil-avatar-badge {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 22px;
  height: 22px;
  background: #34a853;
  border-radius: 50%;
  border: 2px solid #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}
.profil-header-info {
  flex: 1;
  min-width: 0;
}
.profil-header-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
  line-height: 1.3;
}
.profil-header-role {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 2px 0 0;
}
.profil-header-action {
  margin-left: auto;
}
.profil-security-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  color: #4285f4;
  background: rgba(66, 133, 244, 0.08);
  border: 1.5px solid rgba(66, 133, 244, 0.2);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.profil-security-btn:hover {
  background: rgba(66, 133, 244, 0.14);
  border-color: rgba(66, 133, 244, 0.35);
}

/* ===== Alert ===== */
.profil-alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: #fff4e5;
  border: 1px solid #ffd699;
  border-radius: 12px;
  margin-bottom: 20px;
  color: #b45309;
  font-size: 14px;
  font-weight: 500;
}
.profil-alert-icon {
  flex-shrink: 0;
  color: #f59e0b;
}

/* ===== Warning ===== */
.profil-warning {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #fef3c7, #fef9c3);
  border: 1px solid #fcd34d;
  border-radius: 12px;
  margin-bottom: 20px;
}
.profil-warning-icon {
  flex-shrink: 0;
  color: #d97706;
  margin-top: 2px;
}
.profil-warning-title {
  font-size: 14px;
  font-weight: 700;
  color: #92400e;
  margin: 0 0 2px;
}
.profil-warning-text {
  font-size: 13px;
  color: #a16207;
  margin: 0;
}

/* ===== Card ===== */
.profil-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
}
.profil-card-header {
  padding: 24px 28px 0;
}
.profil-card-header-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding-bottom: 24px;
  user-select: none;
}
.profil-card-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 4px;
}
.profil-card-desc {
  font-size: 0.8rem;
  color: #9ca3af;
  margin: 0;
}
.profil-card-body {
  padding: 20px 28px 28px;
}

/* ===== Toggle Button ===== */
.profil-toggle-btn {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: none;
  background: #f3f4f6;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}
.profil-toggle-btn:hover {
  background: #e5e7eb;
}
.profil-toggle-open {
  transform: rotate(180deg);
  background: rgba(66, 133, 244, 0.1);
  color: #4285f4;
}

/* ===== Grid ===== */
.profil-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}
.profil-grid:last-child {
  margin-bottom: 0;
}
@media (max-width: 640px) {
  .profil-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

/* ===== Field ===== */
.profil-field {
  display: flex;
  flex-direction: column;
}
.profil-label {
  font-size: 13px;
  font-weight: 600;
  color: #4b5563;
  margin-bottom: 8px;
}
.profil-required {
  color: #ef4444;
}

/* ===== Input ===== */
.profil-input-wrap {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  background: #f9fafb;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  padding: 0 14px;
  transition: all 0.2s ease;
  height: 46px;
}
.profil-input-wrap:focus-within {
  border-color: #4285f4;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.1);
}
.profil-input-disabled {
  background: #f3f4f6;
  cursor: default;
}
.profil-input-disabled:focus-within {
  border-color: #e5e7eb;
  background: #f3f4f6;
  box-shadow: none;
}
.profil-input-icon {
  font-size: 20px;
  color: #9ca3af;
  margin-right: 10px;
  flex-shrink: 0;
}
.profil-input {
  flex: 1;
  width: 100%;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #1f2937;
  height: 100%;
  text-align: left;
}
.profil-input::placeholder {
  color: #c0c5cc;
}
.profil-input:disabled {
  color: #6b7280;
  cursor: default;
  text-align: left !important;
  -webkit-text-fill-color: #6b7280;
  opacity: 1;
}
.profil-input-text {
  font-size: 14px;
  color: #6b7280;
}

/* ===== Actions ===== */
.profil-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 40px;
}
.profil-save-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 28px;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(135deg, #4285f4 0%, #5a9cf6 100%);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(66, 133, 244, 0.3);
}
.profil-save-btn:hover {
  background: linear-gradient(135deg, #3367d6 0%, #4285f4 100%);
  box-shadow: 0 6px 18px rgba(66, 133, 244, 0.4);
  transform: translateY(-1px);
}
.profil-save-btn:active {
  transform: translateY(0);
}

/* ===== Shared Icon Style ===== */
.profil-btn-icon {
  flex-shrink: 0;
}

/* ===== Modal Styles (kept from original) ===== */
.tr-t1 {
  border-bottom: 1px solid #e5e7eb;
  padding: 12px;
  font-size: 14px;
  position: relative;
}
._ohe {
  float: left;
  font-size: 14px;
}
.tr-t2 {
  overflow: hidden;
}
._3o7u {
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
}
._1aml {
  font-size: 15px;
  color: #90949c;
  display: block;
}
.date-tr {
  float: right;
}
</style>
